import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { apiURL } from "../config/config";
import BackButton from "../components/BackButton";
import LoadingOverlay from "../components/LoadingOverlay";
import {
  removeCachedQuotation,
  upsertCachedQuotation,
} from "../utils/quotationCache";

const RequestApproveFlow = () => {
  const navigate = useNavigate();
  const { quotationId } = useParams();
  const [flowDetails, setFlowDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userFlowId, setUserFlowId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const isDeleted = useRef(false);

  // ✅ ฟังก์ชันลบใบ Quotation ล่าสุด (ใช้เมื่อไม่พบ Flow)
  const deleteLatestQuotation = useCallback(async () => {
    if (!quotationId || isDeleted.current) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${apiURL}quotations/${quotationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      removeCachedQuotation(quotationId);
      toast.info("ใบเสนอราคาถูกลบแล้ว");
      isDeleted.current = true;
    } catch (error) {
      console.error("Error deleting latest quotation:", error);
      toast.error("ไม่สามารถลบใบเสนอราคาได้");
    }
  }, [quotationId]);

  // ✅ ดึง Flow ID จาก localStorage
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser?.flow) {
      setUserFlowId(storedUser.flow);
    } else {
      setLoading(false);
      setErrorMessage(
        "ไม่พบ Flow ที่เกี่ยวข้องกับบัญชีของคุณ กรุณาติดต่อ Admin ที่ jakkit@optx.co.th"
      );
      toast.error("ไม่พบ Flow กรุณาติดต่อ Admin => jakkit@optx.co.th");

      if (!isDeleted.current) {
        deleteLatestQuotation();
      }
    }
  }, [deleteLatestQuotation]);

  // ✅ ดึงรายละเอียด Flow
  useEffect(() => {
    if (!userFlowId || isDeleted.current) return;

    const fetchFlowDetails = async () => {
      try {
        const response = await axios.get(`${apiURL}approve-flows/${userFlowId}`);
        if (!response.data.flow) throw new Error("Flow data is empty");
        setFlowDetails(response.data.flow);
      } catch (error) {
        console.error("Error fetching flow details:", error);
        setFlowDetails(null);
        setErrorMessage(
          "ไม่พบ Flow ที่เกี่ยวข้องกับบัญชีของคุณ กรุณาติดต่อ Admin ที่ jakkit@optx.co.th"
        );
        toast.error("ไม่พบ Flow กรุณาติดต่อ Admin => jakkit@optx.co.th");

        if (!isDeleted.current) {
          deleteLatestQuotation();
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFlowDetails();
  }, [userFlowId, deleteLatestQuotation]);

  // ✅ Confirm & Assign Flow
  const handleConfirm = async () => {
    if (!userFlowId) {
      toast.error("ไม่พบ Flow ที่เกี่ยวข้อง กรุณาติดต่อ Admin => jakkit@optx.co.th");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");

      const approvalHierarchy = flowDetails?.approvalHierarchy
        ?.filter((level) => level.level >= 2)
        .map((level) => ({
          level: level.level,
          approver: level.approver,
          status: "Pending",
        }));

      const payload = { quotationId, approvalHierarchy };

      await axios.post(`${apiURL}approvals`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      try {
        const quotationResponse = await axios.get(
          `${apiURL}quotations/${quotationId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        upsertCachedQuotation(quotationResponse.data);
      } catch (refreshError) {
        console.error("Error refreshing quotation cache:", refreshError);
      }

      toast.success("Assign Approve Flow สำเร็จ");
      navigate("/quotations");
    } catch (error) {
      console.error("Error assigning approve flow:", error);
      toast.error("ไม่สามารถ Assign Approve Flow ได้ ");
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ UI
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 md:px-6 py-6 transition-colors duration-300">
      <LoadingOverlay loading={loading || submitting} />

      <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-md p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Approval Flow Details
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              แสดงรายละเอียดลำดับผู้อนุมัติของใบเสนอราคานี้ ซึ่งถูกกำหนดโดยระบบอัตโนมัติ
            </p>
          </div>
          <BackButton label="ย้อนกลับ" />
        </div>

        {/* Main Section */}
        {loading ? (
          null
        ) : errorMessage ? (
          <div className="p-4 border border-red-400 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md text-sm">
            {errorMessage}
          </div>
        ) : flowDetails ? (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Approve Flow Name
              </label>
              <input
                type="text"
                value={flowDetails.name}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                disabled
              />
            </div>

            {/* Approval Hierarchy */}
            {flowDetails.approvalHierarchy?.length > 0 ? (
              <div className="mt-5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">
                  Approval Levels
                </h2>
                <div className="space-y-3">
                  {flowDetails.approvalHierarchy.map((level, index) => (
                    <div
                      key={index}
                      className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0"
                    >
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        Level {level.level}:
                      </span>
                      <input
                        type="text"
                        value={level.approver}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                        disabled
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-3">
                ไม่พบข้อมูลระดับการอนุมัติ
              </p>
            )}

            {/* Action Button */}
            <div className="text-right mt-6">
              <button
                onClick={handleConfirm}
                className={`px-6 py-2.5 rounded-md text-white text-sm font-medium transition ${
                  submitting
                    ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                    : "bg-indigo-500 hover:bg-indigo-600 dark:bg-indigo-600 dark:hover:bg-indigo-500"
                }`}
                disabled={submitting}
              >
                Confirm
              </button>
            </div>
          </>
        ) : (
          <p className="text-red-500 dark:text-red-400 text-sm">
            ไม่พบ Flow ที่เกี่ยวข้องกับบัญชีของคุณ
          </p>
        )}
      </div>
    </div>
  );
};

export default RequestApproveFlow;
